import { httpsCallable } from "firebase/functions";
import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { loadAdminCalendarEvents } from "../data/calendarFeed";
import { loadFeatureFlags } from "../data/featureFlags";
import { db, functions } from "../firebase";

type ParentInviteStats = {
  total: number;
  notInvited: number;
  invited: number;
  activated: number;
  missingEmail: number;
  inactive: number;
};

type AnalyticsOverview = {
  activeUsers: number;
  sessions: number;
  pageViews: number;
  engagementRate: number;
};

type NewsSummary = {
  id: string;
  title: string;
  summary: string;
  urgent: boolean;
  pinned: boolean;
};

type EventSummary = {
  id: string;
  title: string;
  dateLabel: string;
  location: string;
};

type CrmOverview = {
  openInquiries: number;
  overdueFollowUps: number;
  familyRecords: number;
  recentInteractions: number;
};

type FamilyCrmOverview = {
  trackedFamilies: number;
  overdueFollowUps: number;
  openFollowUps: number;
  recentInteractions: number;
};

type MessagingOverview = {
  unreadThreads: number;
  recentThreads: number;
  directThreads: number;
  broadcastThreads: number;
};

type DonationOverview = {
  activeCampaigns: number;
  donorCount: number;
  totalRaised: number;
  totalGoal: number;
};

function money(value: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
}

function percent(value: number, total: number) {
  if (!total || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)));
}

function KpiTile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="dashboard-kpi-tile">
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}

function MeterBar({ label, value, total, tone = "blue" }: { label: string; value: number; total: number; tone?: "blue" | "gold" | "green" | "red" }) {
  const pct = percent(value, total);
  return (
    <div className="dashboard-meter">
      <div className="dashboard-meter-top">
        <span>{label}</span>
        <b>{value.toLocaleString()}</b>
      </div>
      <div className="dashboard-meter-track">
        <div className={`dashboard-meter-fill dashboard-meter-${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function DonutChart({
  value,
  total,
  label,
  sublabel,
  tone = "blue",
}: {
  value: number;
  total: number;
  label: string;
  sublabel: string;
  tone?: "blue" | "gold" | "green" | "red";
}) {
  const pct = percent(value, total);
  return (
    <div className="dashboard-donut-wrap">
      <div className={`dashboard-donut dashboard-donut-${tone}`} style={{ "--pct": `${pct}%` } as CSSProperties}>
        <div>
          <b>{pct}%</b>
          <span>{label}</span>
        </div>
      </div>
      <div className="muted">{sublabel}</div>
    </div>
  );
}

function SparkBars({ values }: { values: number[] }) {
  const max = Math.max(...values, 1);
  return (
    <div className="dashboard-sparkbars" aria-hidden="true">
      {values.map((value, index) => (
        <span key={index} style={{ height: `${Math.max(12, Math.round((value / max) * 64))}px` }} />
      ))}
    </div>
  );
}

export function Dashboard() {
  const { user, hasPermission } = useAuth();
  const email = user?.email ?? "(unknown)";
  const canNews = hasPermission("news", "view");
  const canCalendar = hasPermission("calendar", "view");
  const canAnalytics = hasPermission("publicWebAnalytics", "view");
  const canUsers = hasPermission("users", "view");
  const canAdmissionsCrm = hasPermission("admissionsCrm", "view");
  const canFamilyCrmPermission = hasPermission("familyCrm", "view");
  const canMessaging = hasPermission("messaging", "view");
  const canDonorCrm = hasPermission("donorCrm", "view");
  const canCrmOverview = canAdmissionsCrm;

  const [familyCrmEnabled, setFamilyCrmEnabled] = useState(true);
  const [parentInviteStats, setParentInviteStats] = useState<ParentInviteStats>({
    total: 0,
    notInvited: 0,
    invited: 0,
    activated: 0,
    missingEmail: 0,
    inactive: 0,
  });
  const [inviteStatsLoading, setInviteStatsLoading] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  const [analyticsMessage, setAnalyticsMessage] = useState("Analytics is not available yet.");
  const [news, setNews] = useState<NewsSummary[]>([]);
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [crm, setCrm] = useState<CrmOverview | null>(null);
  const [familyCrm, setFamilyCrm] = useState<FamilyCrmOverview | null>(null);
  const [messaging, setMessaging] = useState<MessagingOverview | null>(null);
  const [donations, setDonations] = useState<DonationOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const canFamilyCrm = canFamilyCrmPermission && familyCrmEnabled;

  useEffect(() => {
    let cancelled = false;
    loadFeatureFlags()
      .then((flags) => {
        if (!cancelled) setFamilyCrmEnabled(flags.familyCrmEnabled);
      })
      .catch(() => {
        if (!cancelled) setFamilyCrmEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!canUsers) return;
    let cancelled = false;
    (async () => {
      setInviteStatsLoading(true);
      try {
        const fn = httpsCallable(functions, "getParentInviteStats");
        const res: any = await fn({});
        if (!cancelled) {
          setParentInviteStats(
            res?.data?.stats ?? {
              total: 0,
              notInvited: 0,
              invited: 0,
              activated: 0,
              missingEmail: 0,
              inactive: 0,
            }
          );
        }
      } catch {
        if (!cancelled) {
          setParentInviteStats({ total: 0, notInvited: 0, invited: 0, activated: 0, missingEmail: 0, inactive: 0 });
        }
      } finally {
        if (!cancelled) setInviteStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUsers]);

  useEffect(() => {
    if (!canNews && !canCalendar && !canCrmOverview && !canFamilyCrm && !canMessaging && !canDonorCrm && !canAnalytics) return;
    let cancelled = false;

    async function loadOverview() {
      setOverviewLoading(true);
      try {
        const work: Array<Promise<void>> = [];

        if (canNews) {
          work.push(
            getDocs(query(collection(db, "newsItems"), where("status", "==", "published"), orderBy("publishedAt", "desc"), limit(3)))
              .then((snap) => {
                if (cancelled) return;
                setNews(snap.docs.map((item) => {
                  const data: any = item.data();
                  return {
                    id: item.id,
                    title: String(data.title || "School News"),
                    summary: String(data.summary || data.excerpt || ""),
                    urgent: data.urgent === true,
                    pinned: data.pinned === true,
                  };
                }));
              })
              .catch(() => {
                if (!cancelled) setNews([]);
              })
          );
        }

        if (canCalendar) {
          work.push(
            loadAdminCalendarEvents(3)
              .then(({ events: calendarEvents }) => {
                if (cancelled) return;
                setEvents(calendarEvents.map((item) => ({
                  id: item.id,
                  title: item.title,
                  dateLabel: item.dateLabel,
                  location: item.location,
                })));
              })
              .catch(() => {
                if (!cancelled) setEvents([]);
              })
          );
        }

        if (canCrmOverview) {
          work.push(
            Promise.all([
              getDocs(query(collection(db, "publicInquiries"), orderBy("createdAt", "desc"), limit(300))).catch(() => null),
              getDocs(query(collection(db, "familyCrmRecords"), orderBy("updatedAt", "desc"), limit(500))).catch(() => null),
              getDocs(query(collection(db, "familyCrmInteractions"), orderBy("createdAt", "desc"), limit(100))).catch(() => null),
            ]).then(([inquirySnap, familySnap, interactionSnap]) => {
              if (cancelled) return;
              const inquiries = inquirySnap?.docs.map((item) => item.data() as any) || [];
              const open = inquiries.filter((item) => !["closed", "notFit", "enrolled"].includes(item.status || "new"));
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const overdue = open.filter((item) => {
                if (!item.nextFollowUpAt) return false;
                const date = new Date(`${item.nextFollowUpAt}T00:00:00`);
                return Number.isFinite(date.getTime()) && date.getTime() < today.getTime();
              });
              setCrm({
                openInquiries: open.length,
                overdueFollowUps: overdue.length,
                familyRecords: familySnap?.size || 0,
                recentInteractions: interactionSnap?.size || 0,
              });
            })
          );
        }

        if (canFamilyCrm) {
          work.push(
            Promise.all([
              getDocs(query(collection(db, "familyCrmRecords"), orderBy("updatedAt", "desc"), limit(500))).catch(() => null),
              getDocs(query(collection(db, "familyCrmInteractions"), orderBy("createdAt", "desc"), limit(100))).catch(() => null),
            ]).then(([recordSnap, interactionSnap]) => {
              if (cancelled) return;
              const records = recordSnap?.docs.map((item) => item.data() as any) || [];
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const openFollowUps = records.filter((item) => String(item.nextFollowUpAt || "").trim());
              const overdueFollowUps = openFollowUps.filter((item) => {
                const date = new Date(`${item.nextFollowUpAt}T00:00:00`);
                return Number.isFinite(date.getTime()) && date.getTime() < today.getTime();
              });
              setFamilyCrm({
                trackedFamilies: recordSnap?.size || 0,
                openFollowUps: openFollowUps.length,
                overdueFollowUps: overdueFollowUps.length,
                recentInteractions: interactionSnap?.size || 0,
              });
            })
          );
        } else {
          setFamilyCrm(null);
        }

        if (canMessaging) {
          work.push(
            httpsCallable(functions, "getInboxThreads")({ limit: 250 })
              .then((res: any) => {
                if (cancelled) return;
                const threads = Array.isArray(res?.data?.threads) ? res.data.threads : [];
                setMessaging({
                  unreadThreads: threads.filter((item: any) => item?.hasUnread === true).length,
                  recentThreads: threads.length,
                  directThreads: threads.filter((item: any) => String(item?.type || "direct") === "direct").length,
                  broadcastThreads: threads.filter((item: any) => String(item?.type || "") === "broadcast").length,
                });
              })
              .catch(() => {
                if (!cancelled) setMessaging(null);
              })
          );
        }

        if (canDonorCrm) {
          work.push(
            Promise.all([
              getDocs(collection(db, "publicDonationCampaigns")).catch(() => null),
              getDocs(collection(db, "donors")).catch(() => null),
            ]).then(([campaignSnap, donorSnap]) => {
              if (cancelled) return;
              const campaigns = campaignSnap?.docs.map((item) => item.data() as any) || [];
              setDonations({
                activeCampaigns: campaigns.filter((item) => item.status !== "closed").length,
                donorCount: donorSnap?.size || 0,
                totalRaised: campaigns.reduce((sum, item) => sum + Number(item.raisedAmount || 0), 0),
                totalGoal: campaigns.reduce((sum, item) => sum + Number(item.goalAmount || 0), 0),
              });
            })
          );
        }

        if (canAnalytics) {
          work.push(
            httpsCallable(functions, "getPublicWebAnalyticsSummary")({})
              .then((res: any) => {
                if (cancelled) return;
                if (res?.data?.configured === false) {
                  setAnalytics(null);
                  setAnalyticsMessage(String(res?.data?.message || "Analytics is not configured yet."));
                  return;
                }
                const overview = res?.data?.overview;
                setAnalytics(overview ? {
                  activeUsers: Number(overview.activeUsers || 0),
                  sessions: Number(overview.sessions || 0),
                  pageViews: Number(overview.pageViews || 0),
                  engagementRate: Number(overview.engagementRate || 0),
                } : null);
                setAnalyticsMessage("Analytics is not available yet.");
              })
              .catch((error: any) => {
                if (!cancelled) {
                  setAnalytics(null);
                  setAnalyticsMessage(error?.message || "Analytics is not available yet.");
                }
              })
          );
        }

        await Promise.all(work);
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    }

    void loadOverview();
    return () => {
      cancelled = true;
    };
  }, [canNews, canCalendar, canCrmOverview, canFamilyCrm, canMessaging, canDonorCrm, canAnalytics]);

  return (
    <div className="card dashboard-shell">
      <h2>Dashboard</h2>
      <p className="muted">
        Signed in as <b>{email}</b>
      </p>

      <hr className="hr" />

      <div className="dashboard-grid">
        {canAnalytics ? (
          <div className="card dashboard-panel">
            <h3>Public Web Analytics</h3>
            {overviewLoading ? <p className="muted">Loading...</p> : analytics ? (
              <div className="dashboard-chart-card">
                <SparkBars values={[analytics.activeUsers, analytics.sessions, analytics.pageViews, Math.max(analytics.engagementRate, 1)]} />
                <div className="grid2">
                  <KpiTile label="Active visitors" value={analytics.activeUsers.toLocaleString()} />
                  <KpiTile label="Page views" value={analytics.pageViews.toLocaleString()} />
                  <KpiTile label="Sessions" value={analytics.sessions.toLocaleString()} />
                  <KpiTile label="Engagement" value={`${analytics.engagementRate}%`} />
                </div>
              </div>
            ) : <p className="muted">{analyticsMessage}</p>}
            <Link to="/public-web/analytics" className="btn secondary" style={{ marginTop: 12 }}>View Analytics</Link>
          </div>
        ) : null}

        {canUsers ? (
          <div className="card dashboard-panel">
            <h3>Parent Invite Readiness</h3>
            {inviteStatsLoading ? (
              <div className="muted">Loading...</div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                <DonutChart
                  value={parentInviteStats.activated}
                  total={Math.max(parentInviteStats.total - parentInviteStats.inactive, 0)}
                  label="active"
                  sublabel={`${parentInviteStats.activated.toLocaleString()} activated of ${Math.max(parentInviteStats.total - parentInviteStats.inactive, 0).toLocaleString()} active parents`}
                  tone="green"
                />
                <MeterBar label="Not invited" value={parentInviteStats.notInvited} total={parentInviteStats.total} tone="gold" />
                <MeterBar label="Invited, not activated" value={parentInviteStats.invited} total={parentInviteStats.total} tone="blue" />
                <MeterBar label="Missing email" value={parentInviteStats.missingEmail} total={parentInviteStats.total} tone="red" />
                <div style={{ marginTop: 8 }}>
                  <Link to="/users" className="btn secondary">Manage Parent Invites</Link>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {canCrmOverview ? (
          <div className="card dashboard-panel">
            <h3>Admissions CRM</h3>
            {crm ? (
              <div className="dashboard-chart-card">
                <div className="grid2">
                  <KpiTile label="Open inquiries" value={crm.openInquiries} />
                  <KpiTile label="Overdue follow-ups" value={crm.overdueFollowUps} />
                  <KpiTile label="Family records" value={crm.familyRecords} />
                  <KpiTile label="Recent interactions" value={crm.recentInteractions} />
                </div>
                <MeterBar label="Follow-up pressure" value={crm.overdueFollowUps} total={Math.max(crm.openInquiries, 1)} tone={crm.overdueFollowUps ? "red" : "green"} />
                <MeterBar label="Recent touchpoints" value={crm.recentInteractions} total={Math.max(crm.familyRecords, crm.recentInteractions, 1)} tone="blue" />
              </div>
            ) : <p className="muted">CRM summary is not available yet.</p>}
            {canAdmissionsCrm ? <Link to="/public-web/inquiries" className="btn secondary" style={{ marginTop: 12 }}>Open Admissions CRM</Link> : null}
          </div>
        ) : null}

        {canDonorCrm ? (
          <div className="card dashboard-panel">
            <h3>Donors & Campaigns</h3>
            {donations ? (
              <div className="dashboard-chart-card">
                <DonutChart
                  value={donations.totalRaised}
                  total={donations.totalGoal}
                  label="raised"
                  sublabel={`${money(donations.totalRaised)} of ${money(donations.totalGoal)} total campaign goals`}
                  tone="gold"
                />
                <div className="grid2">
                  <KpiTile label="Active campaigns" value={donations.activeCampaigns} />
                  <KpiTile label="Donors" value={donations.donorCount} />
                </div>
                <MeterBar label="Campaign progress" value={donations.totalRaised} total={donations.totalGoal} tone="gold" />
              </div>
            ) : <p className="muted">Donation summary is not available yet.</p>}
            <Link to="/public-web/donations" className="btn secondary" style={{ marginTop: 12 }}>Manage Giving</Link>
          </div>
        ) : null}

        {canMessaging ? (
          <div className="card dashboard-panel">
            <h3>Messaging</h3>
            {messaging ? (
              <div className="dashboard-chart-card">
                <div className="grid2">
                  <KpiTile label="Unread threads" value={messaging.unreadThreads} />
                  <KpiTile label="Recent threads" value={messaging.recentThreads} />
                  <KpiTile label="Direct" value={messaging.directThreads} />
                  <KpiTile label="Broadcast" value={messaging.broadcastThreads} />
                </div>
                <MeterBar label="Unread pressure" value={messaging.unreadThreads} total={Math.max(messaging.recentThreads, 1)} tone={messaging.unreadThreads ? "red" : "green"} />
              </div>
            ) : <p className="muted">Messaging summary is not available yet.</p>}
            <Link to="/messaging" className="btn secondary" style={{ marginTop: 12 }}>Open Messaging</Link>
          </div>
        ) : null}

        {canFamilyCrm ? (
          <div className="card dashboard-panel">
            <h3>Family CRM</h3>
            {familyCrm ? (
              <div className="dashboard-chart-card">
                <div className="grid2">
                  <KpiTile label="Tracked families" value={familyCrm.trackedFamilies} />
                  <KpiTile label="Open follow-ups" value={familyCrm.openFollowUps} />
                  <KpiTile label="Overdue" value={familyCrm.overdueFollowUps} />
                  <KpiTile label="Recent notes" value={familyCrm.recentInteractions} />
                </div>
                <MeterBar label="Follow-up pressure" value={familyCrm.overdueFollowUps} total={Math.max(familyCrm.openFollowUps, 1)} tone={familyCrm.overdueFollowUps ? "red" : "green"} />
              </div>
            ) : <p className="muted">Family CRM summary is not available yet.</p>}
            <Link to="/family-crm" className="btn secondary" style={{ marginTop: 12 }}>Open Family CRM</Link>
          </div>
        ) : null}

        {canNews ? (
          <div className="card dashboard-panel">
            <h3>Latest News</h3>
            {news.length ? (
              <div className="stack">
                {news.map((item) => (
                  <div key={item.id} className="notice-light">
                    <b>{item.urgent ? "Urgent: " : item.pinned ? "Pinned: " : ""}{item.title}</b>
                    {item.summary ? <div className="muted">{item.summary}</div> : null}
                  </div>
                ))}
              </div>
            ) : <p className="muted">No published news yet.</p>}
            <Link to="/news" className="btn secondary" style={{ marginTop: 12 }}>Open News</Link>
          </div>
        ) : null}

        {canCalendar ? (
          <div className="card dashboard-panel">
            <h3>Upcoming Calendar</h3>
            {events.length ? (
              <div className="stack">
                {events.map((item) => (
                  <div key={item.id} className="notice-light">
                    <b>{item.dateLabel}: {item.title}</b>
                    {item.location ? <div className="muted">{item.location}</div> : null}
                  </div>
                ))}
              </div>
            ) : <p className="muted">No upcoming synced events to show.</p>}
            <Link to="/calendar" className="btn secondary" style={{ marginTop: 12 }}>Open Calendar</Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
